@let overview = overview$ | async;
@let items = overview === null ? null : overview.items;
@let isDetecting = overview !== null && (overview.detectionStatus === 'ONGOING' || isAwaitingDetection(overview));
<div class="toolbar">
  <div class="views">
    @if (!isDetecting) {
      @for (option of views; track option.view) {
        <button type="button" class="view" [class.active]="view === option.view" (click)="view = option.view">{{ option.labelKey | translate }}</button>
      }
    }
  </div>
  <div class="toolbar-actions">
    @if (canUserEdit && !isDetecting) {
      <button type="button" class="add-action" (click)="addAction()">
        <mat-icon>add</mat-icon>
        {{ 'ACTION_ITEMS.ADD_ACTION' | translate }}
      </button>
    }
    @if (overview !== null && !isDetecting) {
      <button mat-icon-button class="export" (click)="exportXlsx()">
        <mat-icon>download</mat-icon>
      </button>
    }
  </div>
</div>

@if (isDetecting) {
  <div class="detection-status">
    <span class="spinner"></span>
    <span translate="ACTION_ITEMS.DETECTING"></span>
  </div>
  <div class="table skeleton" aria-hidden="true">
    @for (row of skeletonRows; track $index) {
      <div class="row">
        <div class="skeleton-description">
          @for (width of row.lines; track $index) {
            <span class="bar" [style.width]="width"></span>
          }
        </div>
        <div class="cell skeleton-assignee">
          <span class="bar avatar"></span>
          <span class="bar" [style.width.px]="row.assigneeWidth"></span>
        </div>
        <div class="cell skeleton-due">
          <span class="bar" [style.width.px]="row.dueWidth"></span>
        </div>
        <div class="cell"></div>
      </div>
    }
  </div>
} @else if (overview !== null && overview.detectionStatus === 'FAILED') {
  <div class="detection-status failed">
    <mat-icon>error_outline</mat-icon>
    <span translate="ACTION_ITEMS.DETECTION_FAILED"></span>
  </div>
}

@if (items === null) {
  <app-progress-loader class="loader" indeterminate></app-progress-loader>
} @else if (!isDetecting) {
  <div class="table-scroll">
    <div class="table">
      @if (view === 'BY_TYPE') {
        @for (group of groups(items); track group.key) {
          <button type="button" class="group-header" [attr.aria-expanded]="!isCollapsed(group.key)" (click)="toggleGroup(group.key)">
            <mat-icon class="chevron">{{ isCollapsed(group.key) ? 'chevron_right' : 'expand_more' }}</mat-icon>
            <span class="dot" [attr.data-type]="group.key"></span>
            <span class="group-label">{{ 'ACTION_ITEMS.GROUPS.' + group.key | translate }}</span>
            <span class="group-count">{{ group.items.length }}</span>
            <span class="group-hint">{{ 'ACTION_ITEMS.HINTS.' + group.key | translate }}</span>
          </button>
          @if (!isCollapsed(group.key)) {
            @if (group.key === 'AGREED' && draft !== null) {
              <ng-container *ngTemplateOutlet="draftRow"></ng-container>
            }
            @for (item of group.items; track item.id) {
              <ng-container *ngTemplateOutlet="row; context: { $implicit: item, showDot: false }"></ng-container>
            }
          }
        }
      } @else {
        @if (draft !== null) {
          <ng-container *ngTemplateOutlet="draftRow"></ng-container>
        }
        @for (item of ordered(items); track item.id) {
          <ng-container *ngTemplateOutlet="row; context: { $implicit: item, showDot: true }"></ng-container>
        } @empty {
          @if (draft === null) {
            <div class="empty" translate="ACTION_ITEMS.EMPTY"></div>
          }
        }
      }
    </div>
  </div>
}

<ng-template #draftRow>
  @if (draft; as draftItem) {
    <div class="row draft">
      <div class="description-cell">
        <input
          #draftInput
          class="draft-input"
          [(ngModel)]="draftItem.description"
          (keydown)="onDraftKeydown($event)"
          [placeholder]="'ACTION_ITEMS.DESCRIBE_THE_ACTION' | translate"
        />
      </div>
      <div class="cell">
        <button
          type="button"
          class="cell-button"
          [matMenuTriggerFor]="draftAssigneeMenu"
          (menuOpened)="openAssignees('DRAFT', draftItem.assignees)"
          (menuClosed)="closeDraftAssignees()"
        >
          <ng-container *ngTemplateOutlet="people; context: { $implicit: shownAssignees('DRAFT', draftItem.assignees) }"></ng-container>
        </button>
        <mat-menu #draftAssigneeMenu="matMenu" class="action-item-menu">
          <ng-container *ngTemplateOutlet="assigneeMenuBody; context: { $implicit: draftItem.assignees }"></ng-container>
        </mat-menu>
      </div>
      <div class="cell">
        <button
          type="button"
          class="cell-button due"
          #draftDueTrigger="matMenuTrigger"
          [matMenuTriggerFor]="draftDueMenu"
          (menuOpened)="openDue({ dueDate: draftItem.dueDate, dueText: null })"
        >
          <ng-container *ngTemplateOutlet="dueLabelCell; context: { $implicit: due({ dueDate: draftItem.dueDate, dueText: null }) }"></ng-container>
        </button>
        <mat-menu #draftDueMenu="matMenu" class="action-item-menu" xPosition="before">
          <ng-container *ngTemplateOutlet="dueMenuBody; context: { item: null, dueText: null, trigger: draftDueTrigger }"></ng-container>
        </mat-menu>
      </div>
      <div class="cell row-actions">
        <button mat-icon-button class="save-draft" (click)="saveDraft()" [matTooltip]="'COMMON.ADD' | translate">
          <mat-icon>check</mat-icon>
        </button>
        <button mat-icon-button (click)="cancelDraft()" [matTooltip]="'COMMON.CANCEL' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  }
</ng-template>

<ng-template #row let-item let-showDot="showDot">
  <div class="row" [class.not-an-action]="item.isMarkedAsNotAnAction">
    <div class="description-cell">
      @if (showDot) {
        <span
          class="dot row-dot"
          [attr.data-type]="item.isMarkedAsNotAnAction ? 'NOT_AN_ACTION' : item.type"
          [matTooltip]="'ACTION_ITEMS.GROUPS.' + (item.isMarkedAsNotAnAction ? 'NOT_AN_ACTION' : item.type) | translate"
        ></span>
      }
      <div class="description-body">
        @if (editingId === item.id) {
          <textarea #editInput class="edit-input" rows="2" [(ngModel)]="editValue" (keydown)="onEditKeydown($event, item)" (blur)="saveEdit(item)"></textarea>
          <span class="note" translate="ACTION_ITEMS.EDIT_HINT"></span>
        } @else {
          <div class="description" [class.editable]="canUserEdit && !item.isMarkedAsNotAnAction && detailLoadingId !== item.id" (click)="startEdit(item)">
            <span [class.generating]="detailLoadingId === item.id">{{ item.description }}</span>
            @for (passage of item.passages; track passage.startSentenceId; let index = $index) {
              <button
                type="button"
                class="footnote"
                [class.active]="activePassageKey === passageKey(item, passage)"
                [matTooltip]="passage.text"
                matTooltipClass="mask-transcript-passage"
                (click)="$event.stopPropagation(); showPassage(item, passage)"
              >
                {{ index + 1 }}
              </button>
            }
            @if (canAddDetail(item)) {
              <button
                type="button"
                class="add-detail"
                [matTooltip]="'ACTION_ITEMS.ADD_DETAIL' | translate"
                [attr.aria-label]="'ACTION_ITEMS.ADD_DETAIL' | translate"
                (click)="$event.stopPropagation(); addDetail(item)"
              >
                <mat-icon>add</mat-icon>
              </button>
            }
          </div>
        }
        @if (detailLoadingId === item.id) {
          <div class="detail-placeholder" aria-hidden="true">
            <span class="bar"></span>
            <span class="bar"></span>
          </div>
          <span class="note detail-status">
            <span class="spinner"></span>
            {{ 'ACTION_ITEMS.ADDING_DETAIL' | translate }}
            <span class="separator">·</span>
            <button type="button" class="link" (click)="cancelDetail()">{{ 'COMMON.CANCEL' | translate }}</button>
          </span>
        }
        @if (descriptionBeforeDetail.has(item.id)) {
          <span class="note edited">
            <mat-icon>edit</mat-icon>
            {{ 'ACTION_ITEMS.DETAIL_ADDED' | translate }}
            <button type="button" class="link" (click)="undoDetail(item)">{{ 'ACTION_ITEMS.UNDO' | translate }}</button>
          </span>
        } @else if (editedNoteKey(item); as noteKey) {
          <span class="note edited">
            <mat-icon>edit</mat-icon>
            {{ noteKey | translate }}
          </span>
        }
      </div>
    </div>

    <div class="cell">
      <button
        type="button"
        class="cell-button"
        [disabled]="!canUserEdit || item.isMarkedAsNotAnAction"
        [matMenuTriggerFor]="assigneeMenu"
        (menuOpened)="openAssignees(item.id, item.assignees)"
        (menuClosed)="saveAssignees(item)"
      >
        <ng-container *ngTemplateOutlet="people; context: { $implicit: shownAssignees(item.id, item.assignees) }"></ng-container>
      </button>
      <mat-menu #assigneeMenu="matMenu" class="action-item-menu">
        <ng-container *ngTemplateOutlet="assigneeMenuBody; context: { $implicit: item.assignees }"></ng-container>
      </mat-menu>
    </div>

    <div class="cell">
      <button
        type="button"
        class="cell-button due"
        #dueTrigger="matMenuTrigger"
        [disabled]="!canUserEdit || item.isMarkedAsNotAnAction"
        [matMenuTriggerFor]="dueMenu"
        (menuOpened)="openDue(item)"
      >
        <ng-container *ngTemplateOutlet="dueLabelCell; context: { $implicit: due(item) }"></ng-container>
      </button>
      <mat-menu #dueMenu="matMenu" class="action-item-menu" xPosition="before">
        <ng-container *ngTemplateOutlet="dueMenuBody; context: { item: item, dueText: item.dueText, trigger: dueTrigger }"></ng-container>
      </mat-menu>
    </div>

    <div class="cell row-actions">
      @if (canUserEdit) {
        @if (item.isMarkedAsNotAnAction) {
          <button mat-button class="restore" (click)="restore(item)">{{ 'ACTION_ITEMS.RESTORE' | translate }}</button>
        } @else {
          <button
            mat-icon-button
            class="mark"
            [matTooltip]="'ACTION_ITEMS.NOT_AN_ACTION' | translate"
            [attr.aria-label]="'ACTION_ITEMS.NOT_AN_ACTION' | translate"
            (click)="markAsNotAnAction(item)"
          >
            <mat-icon>block</mat-icon>
          </button>
        }
      }
    </div>
  </div>
</ng-template>

<ng-template #people let-assignees>
  @for (assignee of assignees; track $index) {
    <span class="person">
      <app-avatar [input]="assignee.name" size="small"></app-avatar>
      {{ assignee.name }}
    </span>
  } @empty {
    <span class="person muted">
      <mat-icon>person_add</mat-icon>
      {{ 'ACTION_ITEMS.UNASSIGNED' | translate }}
    </span>
  }
</ng-template>

<ng-template #assigneeMenuBody let-assignees>
  <div class="menu-body" (click)="$event.stopPropagation()" (keydown)="keepKeysInMenu($event)">
    <div class="menu-label" translate="ACTION_ITEMS.MEETING_ATTENDEES"></div>
    @for (attendee of attendees; track attendee.id) {
      <button type="button" class="menu-row" (click)="toggleAttendee(attendee)">
        <mat-icon class="check">{{ isAttendeeSelected(attendee) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
        <app-avatar [input]="attendeeName(attendee)" size="small"></app-avatar>
        <span class="person-text">
          <span class="name">{{ attendeeName(attendee) }}</span>
          @if (attendee.role !== null && attendee.role !== '') {
            <span class="role" [title]="attendee.role">{{ attendee.role }}</span>
          }
        </span>
      </button>
    }
    @for (other of otherAssignees(assignees); track other.name) {
      <button type="button" class="menu-row" (click)="toggleName(other.name)">
        <mat-icon class="check">{{ isNameSelected(other.name) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
        <app-avatar [input]="other.name" size="small"></app-avatar>
        <span class="person-text">
          <span class="name">{{ other.name }}</span>
          <span class="role" translate="ACTION_ITEMS.NOT_AN_ATTENDEE"></span>
        </span>
      </button>
    }
    <div class="menu-divider"></div>
    @if (newAssigneeName === null) {
      <button type="button" class="menu-row" (click)="startNewAssignee()">
        <mat-icon class="check">person_add_alt</mat-icon>
        <span class="name" translate="ACTION_ITEMS.ADD_NAME_NOT_IN_LIST"></span>
      </button>
    } @else {
      <div class="new-name">
        <input #newNameInput [(ngModel)]="newAssigneeName" (keydown.enter)="addNewAssignee()" [placeholder]="'ACTION_ITEMS.FULL_NAME' | translate" />
        <button mat-flat-button (click)="addNewAssignee()">{{ 'COMMON.ADD' | translate }}</button>
      </div>
    }
  </div>
</ng-template>

<ng-template #dueLabelCell let-label>
  @if (label.main !== null) {
    <span class="due-main">{{ label.main }}</span>
    @if (label.fromTheMeeting !== null) {
      <span class="due-sub">{{ 'ACTION_ITEMS.FROM_THE_MEETING' | translate: { said: label.fromTheMeeting } }}</span>
    }
  } @else {
    <span class="muted" translate="ACTION_ITEMS.NO_DUE_DATE"></span>
  }
</ng-template>

<ng-template #dueMenuBody let-item="item" let-dueText="dueText" let-trigger="trigger">
  <div class="menu-body due-menu" (click)="$event.stopPropagation()" (keydown)="keepKeysInMenu($event)">
    <div class="menu-title" translate="ACTION_ITEMS.DUE_DATE"></div>
    @if (dueText !== null) {
      <div class="said">
        <span class="muted" translate="ACTION_ITEMS.FROM_THE_MEETING_LABEL"></span>
        {{ dueText }}
      </div>
    }
    <button type="button" class="radio-row" (click)="dueMode = 'AS_SAID'">
      <mat-icon>{{ dueMode === 'AS_SAID' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
      {{ (dueText !== null ? 'ACTION_ITEMS.KEEP_AS_SAID' : 'ACTION_ITEMS.NO_DUE_DATE') | translate }}
    </button>
    <button type="button" class="radio-row" (click)="dueMode = 'DATE'">
      <mat-icon>{{ dueMode === 'DATE' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
      {{ 'ACTION_ITEMS.SET_A_DATE' | translate }}
      @if (dueSelection !== null) {
        <span class="selected-date">{{ formatDate(dueSelection) }}</span>
      }
    </button>
    <mat-calendar
      class="calendar"
      [class.dimmed]="dueMode !== 'DATE'"
      [startAt]="dueSelection"
      [selected]="dueSelection"
      (selectedChange)="selectDueDate($event)"
    ></mat-calendar>
    <div class="menu-footer">
      <button mat-button (click)="trigger.closeMenu()">{{ 'COMMON.CANCEL' | translate }}</button>
      <button mat-flat-button [disabled]="dueMode === 'DATE' && dueSelection === null" (click)="applyDue(item, trigger)">
        {{ 'COMMON.APPLY' | translate }}
      </button>
    </div>
  </div>
</ng-template>
